import type { CalcTab } from '@/pages/Home'
import {Trees, Leaf, TrendingUp, Droplets} from 'lucide-react'

interface SidebarProps {
  activeTab: CalcTab
  setActiveTab: (tab: CalcTab) => void
  mobileOpen: boolean
  setMobileOpen: (v: boolean) => void
}

const NAV_ITEMS: { id: CalcTab; label: string; short: string; icon: React.ReactNode; seo: string; color: string }[] = [
  {
    id: 'spacing',
    label: 'Orchard Spacing',
    short: 'Spacing',
    icon: <Trees size={18} />,
    seo: 'Mango Orchard Spacing Calculator',
    color: '#16a34a',
  },
  {
    id: 'fertilizer',
    label: 'Fertilizer & Nutrients',
    short: 'Fertilizer',
    icon: <Leaf size={18} />,
    seo: 'Mango Fertilizer Calculator',
    color: '#0284c7',
  },
  {
    id: 'yield',
    label: 'Yield & Revenue',
    short: 'Yield',
    icon: <TrendingUp size={18} />,
    seo: 'Mango Tree Yield Calculator',
    color: '#d97706',
  },
  {
    id: 'brix',
    label: 'Brix & Ripeness',
    short: 'Brix',
    icon: <Droplets size={18} />,
    seo: 'Mango Brix Calculator',
    color: '#ea580c',
  },
]

export default function Sidebar({ activeTab, setActiveTab, mobileOpen, setMobileOpen }: SidebarProps) {
  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-40 h-full w-72 flex flex-col border-r shadow-xl
          transition-transform duration-300 ease-in-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:static lg:translate-x-0 lg:shadow-none lg:z-auto
        `}
        style={{ background: '#fffbf2', borderColor: '#e8d9b0' }}
      >
        {/* Logo area */}
        <div className="px-6 py-5 border-b" style={{ borderColor: '#e8d9b0' }}>
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-2xl flex items-center justify-center text-xl shadow-inner"
              style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
            >
              🥭
            </div>
            <div>
              <h1 className="font-extrabold text-sm leading-tight" style={{ color: '#7c2d12' }}>
                Mango Calculator Hub
              </h1>
              <p className="text-xs" style={{ color: '#a8a29e' }}>mangocalculator.com</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <p className="text-xs font-bold uppercase tracking-widest px-3 mb-3" style={{ color: '#a16207' }}>
            Calculators
          </p>
          {NAV_ITEMS.map((item, idx) => {
            const isActive = activeTab === item.id
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setActiveTab(item.id)
                  setMobileOpen(false)
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left transition-all duration-150"
                style={
                  isActive
                    ? {
                        background: item.color + '15',
                        border: `1.5px solid ${item.color}40`,
                        color: item.color,
                      }
                    : {
                        color: '#57534e',
                        border: '1.5px solid transparent',
                      }
                }
              >
                <span
                  className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={
                    isActive
                      ? { background: item.color + '20', color: item.color }
                      : { background: '#f5f5f4', color: '#a8a29e' }
                  }
                >
                  {item.icon}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold leading-tight">{item.label}</p>
                  <p
                    className="text-xs mt-0.5 truncate"
                    style={{ color: isActive ? item.color + 'aa' : '#a8a29e' }}
                  >
                    {item.seo}
                  </p>
                </div>
                <span
                  className="ml-auto text-xs font-bold flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                  style={isActive ? { background: item.color, color: '#fff' } : { background: '#e8d9b0', color: '#78716c' }}
                >
                  {idx + 1}
                </span>
              </button>
            )
          })}
        </nav>

        {/* Bottom badge */}
        <div className="px-4 py-4 border-t" style={{ borderColor: '#e8d9b0' }}>
          <div
            className="rounded-2xl p-3 border text-center"
            style={{ background: '#fef9c3', borderColor: '#fde68a' }}
          >
            <p className="text-xs font-bold" style={{ color: '#92400e' }}>🌏 International Standards</p>
            <p className="text-xs mt-1" style={{ color: '#78716c' }}>FAO · ICAR · Codex Alimentarius</p>
          </div>
        </div>
      </aside>
    </>
  )
}

export { NAV_ITEMS }
